/**
 * Client-portal appointment cancellation helpers (ENG-3443).
 * Client cancellations always cancel the entire appointment.
 */
import type { MedplumClient } from '@medplum/core';
import type { Appointment, AppointmentParticipant } from '@medplum/fhirtypes';
import { CANCELLATION_REASON_SYSTEM } from '@unify/fhir-model/constants';

function isActivePatientParticipant(participant: AppointmentParticipant): boolean {
  const ref = participant.actor?.reference;
  if (!ref?.startsWith('Patient/') && !ref?.startsWith('RelatedPerson/')) return false;
  // Portal policies hide participant extensions (attendance); manual absent-attendance writers set `declined`
  // (chime-auto attendance does not, so the client can rejoin in the join window).
  return participant.status !== 'declined';
}

/** Active Patient and RelatedPerson participants used by appointment list projections. */
export function countActivePatientParticipants(appointment: Appointment): number {
  return (appointment.participant ?? []).filter(isActivePatientParticipant).length;
}

const PORTAL_CANCEL_REASON_CODE = 'client-cancelled';

export function applyPortalCancellation(input: { appointment: Appointment }): Appointment {
  return {
    ...input.appointment,
    status: 'cancelled',
    cancelationReason: {
      coding: [
        { system: CANCELLATION_REASON_SYSTEM, code: PORTAL_CANCEL_REASON_CODE, display: PORTAL_CANCEL_REASON_CODE },
      ],
    },
  };
}

export async function submitPortalAppointmentCancellation(
  medplum: MedplumClient,
  input: {
    appointmentId: string;
  },
): Promise<Appointment> {
  const existing = await medplum.readResource('Appointment', input.appointmentId);
  const next = applyPortalCancellation({ appointment: existing });
  return medplum.updateResource(next);
}
